import {token} from "@atlaskit/tokens";
import {useQuery} from "@tanstack/react-query";
import {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from "react";
import {applyDispositionOptionRules, evaluateSuggestedJourneyStatus} from "~/components/dispositionPluginEngine";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useWorkspaceEventContainerTemplates} from "~/contexts/workspaceContext";
import {OttopilotDispositionEmitWhen,
OttopilotDispositionPluginKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {getLastNDispositionIdsForEventContainer} from "~/utilities/apis/eventContainersApi";
import {
    getEventContainerDispositionColumnId,
    getEventContainerDispositionId,
    requireEventContainerTemplate,
} from "~/utilities/ottopilotSemanticColumns";
import {flatDispositionToDisplayNameFormat} from "~/utilities/utilities";

type DispositionNode = {
    value: string;
    children: Record<string, DispositionNode>;
    isLeaf?: boolean;
};

export type GenericDispositionSelectorOutcome = {
    dispositionId: Uuid | null;
    suggestedJourneyStatus: Uuid | null;
};

export type DispositionSelectionState = "inProgress" | "completed";

export type GenericDispositionSelectorHandle = {
    reset: () => void;
};

const defaultDispositionConfig: OttopilotDispositionConfig = {
    emitWhen: OttopilotDispositionEmitWhen.pathIsFinalLeaf,
    plugins: [],
};

const EMPTY_DISPOSITION_IDS: Array<Uuid> = [];

function dispositionPathsAreEqual(left: Array<string>, right: Array<string>): boolean {
    return left.length === right.length && left.every((segment, index) => segment === right[index]);
}

export function buildTree(flat: Array<string>): Record<string, DispositionNode> {
    const root: Record<string, DispositionNode> = {};

    for (const item of flat) {
        if (item === "") {
            continue;
        }

        const parts = item.split("_");
        let current = root;

        for (let idx = 0; idx < parts.length; idx += 1) {
            const part = parts[idx];
            if (part === undefined) {
                continue;
            }
            if (current[part] === undefined) {
                current[part] = {value: part, children: {}};
            }

            if (idx === parts.length - 1) {
                current[part].isLeaf = true;
            }

            current = current[part].children;
        }
    }

    return root;
}

function isParentPathCompleteForLevel(
    tree: Record<string, DispositionNode>,
    selected: Array<string>,
    level: number,
): boolean {
    if (level === 0) {
        return true;
    }
    let current: Record<string, DispositionNode> = tree;
    for (let i = 0; i < level; i++) {
        const key = selected[i];
        if (key === undefined || key === "") {
            return false;
        }
        const node = current[key];
        if (node === undefined) {
            return false;
        }
        current = node.children;
    }
    return true;
}

export const GenericDispositionSelector = forwardRef<
    GenericDispositionSelectorHandle,
    {
        journey: EventContainerWithEvents;
        workspaceId: Uuid;
        dispositions: Array<{id: Uuid; displayName: string}>;
        value: Uuid | null;
        onOutcomeChange: (outcome: GenericDispositionSelectorOutcome) => void;
        onSelectionStateChange?: (state: DispositionSelectionState) => void;
        dispositionConfig?: OttopilotDispositionConfig;
        alwaysVisibleLevels?: number;
        /** When true, all tiers are non-interactive (config `readonlyWhen`). History still loads. */
        isDisabled?: boolean;
        /** When true, shows required indicator on each visible disposition tier (config `isRequired` / `requiredWhen`). */
        isRequired?: boolean;
    }
>(function GenericDispositionSelector(
    {
        journey,
        workspaceId,
        dispositions,
        value,
        onOutcomeChange,
        onSelectionStateChange,
        dispositionConfig,
        alwaysVisibleLevels,
        isDisabled = false,
        isRequired = false,
    },
    ref,
) {
    // console.log("🚀 ~ GenericDispositionSelector ~ value:", value);
    // console.log("🚀 ~ GenericDispositionSelector ~ selected disposition:", dispositions.find((d) => d.id === value)?.displayName);
    const eventContainerId = journey.id;
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const eventContainerTemplates = useWorkspaceEventContainerTemplates();
    const eventContainerTemplate = requireEventContainerTemplate(eventContainerTemplates, journey.templateId);
    const dispositionColumnId = getEventContainerDispositionColumnId(eventContainerTemplate);
    const finalDispositionConfig = dispositionConfig ?? defaultDispositionConfig;

    let historyPlugin: {kind: OttopilotDispositionPluginKind.dispositionHistory; lastN: number} | undefined;
    for (const plugin of finalDispositionConfig.plugins) {
        if (plugin.kind === OttopilotDispositionPluginKind.dispositionHistory) {
            historyPlugin = plugin;
            break;
        }
    }

    const dispositionHistoryQuery = useQuery({
        queryKey: ["getLastNDispositionIdsForEventContainer", eventContainerId, workspaceId, dispositionColumnId, historyPlugin?.lastN],
        enabled: historyPlugin !== undefined,
        queryFn: async () => {
            if (historyPlugin === undefined) {
                return [];
            }
            const result = await getLastNDispositionIdsForEventContainer({
                eventContainerId,
                dispositionColumnId: dispositionColumnId,
                workspaceId: workspaceId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                lastNDispositionChangesLimit: historyPlugin.lastN,
                templateId: journey.templateId,
            });
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
    });

    const previousDispositionIds = dispositionHistoryQuery.data ?? EMPTY_DISPOSITION_IDS;

    const effectiveCurrentDispositionId = value ?? getEventContainerDispositionId(journey, eventContainerTemplate);

    const tree = useMemo(() => {
        const flat = dispositions.map((d) => d.displayName);
        return buildTree(flat);
    }, [dispositions]);

    const [selected, setSelected] = useState<Array<string>>([]);

    useImperativeHandle(
        ref,
        () => ({
            reset() {
                setSelected([]);
                onSelectionStateChange?.("completed");
                onOutcomeChange({
                    dispositionId: null,
                    suggestedJourneyStatus: null,
                });
            },
        }),
        [onOutcomeChange, onSelectionStateChange],
    );

    useEffect(() => {
        if (value === null) {
            return;
        }
        const matched = dispositions.find((d) => d.id === value);
        if (matched !== undefined) {
            const nextSelected = matched.displayName.split("_");
            setSelected((currentSelected) =>
                dispositionPathsAreEqual(currentSelected, nextSelected) ? currentSelected : nextSelected,
            );
        }
    }, [value, dispositions]);

    function getSelectedNode(): DispositionNode | null {
        let current: Record<string, DispositionNode> = tree;
        let node: DispositionNode | null = null;

        for (const key of selected) {
            if (current[key] === undefined) {
                return null;
            }
            node = current[key];
            current = node.children;
        }

        return node;
    }

    function getDispositionIdForPath(path: Array<string>): Uuid | null {
        if (path.length === 0) {
            return null;
        }
        const flat = path.join("_");
        const matched = dispositions.find((d) => d.displayName === flat);
        return matched?.id ?? null;
    }

    function getOptions(level: number): Array<{value: string; label: string}> {
        let current: Record<string, DispositionNode> = tree;

        for (let i = 0; i < level; i += 1) {
            const key = selected[i];
            if (key === undefined || current[key] === undefined) {
                return [];
            }
            current = current[key].children;
        }

        const baseOptions = Object.keys(current).map((option) => ({
            value: option,
            label: flatDispositionToDisplayNameFormat(option),
        }));

        return applyDispositionOptionRules({
            options: baseOptions,
            level: level,
            selected: selected,
            previousDispositionIds: previousDispositionIds,
            currentDispositionId: effectiveCurrentDispositionId,
            dispositionConfig: finalDispositionConfig,
        });
    }

    const selectedNode = getSelectedNode();
    const isFinalDisposition =
        selectedNode !== null && (selectedNode.isLeaf === true || Object.keys(selectedNode.children).length === 0);
    const currentPathDispositionId = getDispositionIdForPath(selected);
    const canEmitOutcome =
        currentPathDispositionId !== null &&
        (finalDispositionConfig.emitWhen === OttopilotDispositionEmitWhen.pathMatchesKnownDisposition || isFinalDisposition);

    const selectionState: DispositionSelectionState =
        selected.length > 0 && canEmitOutcome === false ? "inProgress" : "completed";

    useEffect(() => {
        onSelectionStateChange?.(selectionState);
    }, [selectionState, onSelectionStateChange]);

    useEffect(() => {
        if (canEmitOutcome === false || currentPathDispositionId === null) {
            return;
        }

        const suggestedJourneyStatus = evaluateSuggestedJourneyStatus({
            dispositionConfig: finalDispositionConfig,
            selectedDispositionId: currentPathDispositionId,
            previousDispositionIds: previousDispositionIds,
            journeyCreatedAtIso: journey.createdAt,
        });

        onOutcomeChange({
            dispositionId: currentPathDispositionId,
            suggestedJourneyStatus: suggestedJourneyStatus,
        });
    }, [
        canEmitOutcome,
        currentPathDispositionId,
        previousDispositionIds,
        finalDispositionConfig,
        journey.createdAt,
        onOutcomeChange,
    ]);

    const renderTierRow = (level: number, options: Array<{value: string; label: string}>, rowDisabled: boolean) => {
        const slug = selected[level];
        const hasSelection = slug !== undefined && slug !== "";
        const selectOptions =
            rowDisabled !== true && options.length > 0
                ? options.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                  }))
                : [{value: "__noop__", label: "Select", isDisabled: true}];

        return (
            <div
                key={level}
                className="grid grid-flow-col gap-4 grid-cols-[7.75rem_auto]"
            >
                <div
                    className="font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                    style={{
                        color: token("color.text"),
                        font: "Inter",
                    }}
                >
                    Disposition L{level + 1}
                    {isRequired === true ? (
                        <span style={{color: token("color.text.danger")}}> *</span>
                    ) : null}
                </div>
                <AnankeSelect
                    isDisabled={rowDisabled}
                    placeholder="Select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    menuPosition="fixed"
                    maxMenuHeight={280}
                    styles={{
                        menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
                    }}
                    value={
                        rowDisabled === true || hasSelection !== true
                            ? null
                            : {
                                  value: slug,
                                  label: flatDispositionToDisplayNameFormat(slug),
                              }
                    }
                    onChange={
                        rowDisabled === true
                            ? undefined
                            : (newValue: {value: string} | null) => {
                                  if (newValue === null || newValue.value === "__noop__") {
                                      return;
                                  }
                                  setSelected((prev) => {
                                      const next = prev.slice(0, level);
                                      next[level] = newValue.value;
                                      return next;
                                  });
                              }
                    }
                    options={selectOptions}
                />
            </div>
        );
    };

    return (
        <div className="grid gap-3">
            {historyPlugin !== undefined &&
                (dispositionHistoryQuery.isLoading || dispositionHistoryQuery.isPending) && (
                    <div
                        className="font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                        style={{color: token("color.text.subtle"), font: "Inter"}}
                    >
                        Loading disposition history...
                    </div>
                )}
            {historyPlugin !== undefined && dispositionHistoryQuery.isError && (
                <div
                    className="font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                    style={{color: token("color.text.danger"), font: "Inter"}}
                >
                    Failed to load disposition history. You can still select a disposition.
                </div>
            )}
            {alwaysVisibleLevels !== undefined
                ? Array.from({length: alwaysVisibleLevels}, (_, level) => {
                      const parentComplete = isParentPathCompleteForLevel(tree, selected, level);
                      const options = parentComplete ? getOptions(level) : [];
                      const rowDisabled = isDisabled === true || parentComplete !== true || options.length === 0;
                      return renderTierRow(level, options, rowDisabled);
                  })
                : Array.from({length: selected.length + 1}).map((_, level) => {
                      const options = getOptions(level);
                      if (options.length === 0) {
                          return null;
                      }
                      return renderTierRow(level, options, isDisabled === true);
                  })}
        </div>
    );
});
